import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const contactInfo = {
    phone: '0489822709',
    email: 'massa_manu@outlook.com',
    address: 'Av. de la Basilique 14, 1082 Berchem-Sainte-Agathe',
    whatsapp: '0489822709'
  };

  const services = [
    'Coaching Privé',
    'Coaching Duo',
    'Coaching Groupe',
    'Coaching En Ligne',
    'Programme Nutrition',
    'Football Academy'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleWhatsApp = () => {
    const message = formData.name && formData.service
      ? `Bonjour, je suis ${formData.name} et je suis intéressé(e) par ${formData.service}.`
      : 'Bonjour, je souhaite obtenir des informations sur vos services de coaching.';
    
    const whatsappUrl = `https://wa.me/32${contactInfo.whatsapp.substring(1)}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${contactInfo.phone}`;
  };

  const handleEmail = () => {
    const subject = formData.service 
      ? `Demande d'information - ${formData.service}`
      : 'Demande d\'information - EM Coaching';
    
    const body = `Nom: ${formData.name}\nTéléphone: ${formData.phone}\nService: ${formData.service}\n\nMessage:\n${formData.message}`;
    
    window.location.href = `mailto:${contactInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Show success message and provide contact options
    toast.success('Formulaire prêt ! Choisissez votre mode de contact préféré ci-dessous.');
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 to-black"></div>
        <div className="relative container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contactez <span className="text-red-600">Coach Massa</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Prêt à commencer votre transformation ? Réservez votre première séance ou posez-nous vos questions
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-zinc-950 border-t border-red-600">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-zinc-900 p-6 rounded-lg text-center hover:bg-zinc-800 transition-all">
              <Phone className="w-10 h-10 text-red-600 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Téléphone</h3>
              <a href={`tel:${contactInfo.phone}`} className="text-gray-400 hover:text-red-600 transition-colors">
                {contactInfo.phone}
              </a>
            </div>
            <div className="bg-zinc-900 p-6 rounded-lg text-center hover:bg-zinc-800 transition-all">
              <Mail className="w-10 h-10 text-red-600 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Email</h3>
              <a href={`mailto:${contactInfo.email}`} className="text-gray-400 hover:text-red-600 transition-colors break-all">
                {contactInfo.email}
              </a>
            </div>
            <div className="bg-zinc-900 p-6 rounded-lg text-center hover:bg-zinc-800 transition-all">
              <MapPin className="w-10 h-10 text-red-600 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Adresse</h3>
              <p className="text-gray-400">{contactInfo.address}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-zinc-950 rounded-2xl p-8 md:p-12 border border-zinc-800">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">
                Formulaire de <span className="text-red-600">Réservation</span>
              </h2>
              <p className="text-gray-400 text-center mb-8">
                Remplissez le formulaire ci-dessous et choisissez votre mode de contact préféré
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white mb-2 font-medium">
                      Nom Complet <span className="text-red-600">*</span>
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2 font-medium">
                      Email <span className="text-red-600">*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      className="bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white mb-2 font-medium">
                      Téléphone
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="0489 XX XX XX"
                      className="bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2 font-medium">
                      Service Souhaité <span className="text-red-600">*</span>
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                      required
                    >
                      <option value="">Sélectionnez un service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-white mb-2 font-medium">
                    Message
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Parlez-nous de vos objectifs, disponibilités..."
                    rows={5}
                    className="bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 resize-none"
                  />
                </div>

                <div className="bg-zinc-900 p-6 rounded-lg border border-red-600/30">
                  <h3 className="text-white font-semibold mb-4 text-center">
                    Choisissez votre mode de contact préféré :
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Button
                      type="button"
                      onClick={handleWhatsApp}
                      className="bg-green-600 hover:bg-green-700 text-white w-full"
                      size="lg"
                    >
                      <MessageCircle className="mr-2" />
                      WhatsApp
                    </Button>
                    <Button
                      type="button"
                      onClick={handleCall}
                      className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                      size="lg"
                    >
                      <Phone className="mr-2" />
                      Appeler
                    </Button>
                    <Button
                      type="button"
                      onClick={handleEmail}
                      className="bg-red-600 hover:bg-red-700 text-white w-full"
                      size="lg"
                    >
                      <Mail className="mr-2" />
                      Email
                    </Button>
                  </div>
                  <p className="text-gray-400 text-sm text-center mt-4">
                    Votre formulaire sera pré-rempli dans votre application de contact
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-white text-center mb-12">
            Notre <span className="text-red-600">Localisation</span>
          </h2>
          <div className="max-w-5xl mx-auto">
            <div className="aspect-video rounded-xl overflow-hidden border border-zinc-800">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2517.8668896844844!2d4.295819776892989!3d50.86748747166738!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c3c3f5e3b3e3e3%3A0x3e3e3e3e3e3e3e3e!2sAv.%20de%20la%20Basilique%2014%2C%201082%20Berchem-Sainte-Agathe!5e0!3m2!1sfr!2sbe!4v1234567890123!5m2!1sfr!2sbe"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="EM Coaching Location"
              ></iframe>
            </div>
            <div className="mt-6 text-center">
              <p className="text-gray-400 text-lg">{contactInfo.address}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-20 bg-red-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Besoin d'une Réponse Rapide ?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contactez-nous directement sur WhatsApp pour une réponse immédiate
          </p>
          <Button
            size="lg"
            onClick={handleWhatsApp}
            className="bg-black hover:bg-zinc-900 text-white px-8 py-6 text-lg"
          >
            <MessageCircle className="mr-2" />
            Discuter sur WhatsApp
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Contact;
